import axios from "axios";
import { getFieldToken } from "./fieldSession";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  // Send httpOnly auth cookies on every request. Required so the
  // backend's `get_current_user` dependency can read `access_token` /
  // `refresh_token` issued by the JumpCloud OIDC callback.
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Field staff (magic link): bearer token is the primary transport.
API.interceptors.request.use((config) => {
  const t = getFieldToken();
  if (t && !config.headers?.Authorization) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// On 401 from a protected route, try refreshing once. If that also fails,
// bounce the user to /login (unless we're already there or auth is
// bypassed on preview).
let _isRefreshing = false;
let _refreshQueue = [];
API.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config || {};
    const status = err?.response?.status;
    const url = original.url || "";
    if (status === 401 && !original._retry && !url.includes("/auth/")) {
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _refreshQueue.push({ resolve, reject, original });
        });
      }
      original._retry = true;
      _isRefreshing = true;
      try {
        await API.post("/auth/refresh");
        // #83 (L8): each queued request gets `_retry = true` BEFORE
        // being replayed, so if any of them 401 again (e.g. the
        // refresh "succeeded" but the new access token is somehow
        // also rejected) it can't kick off another refresh cycle.
        // Without this, a misconfigured cookie or rotated JWT secret
        // could send the interceptor into a tight loop.
        _refreshQueue.forEach(({ resolve, original: o }) => {
          o._retry = true;
          resolve(API(o));
        });
        _refreshQueue = [];
        return API(original);
      } catch (e) {
        // #(med) (iter168): reset queue + flag BEFORE rejecting so a
        // queued caller that synchronously re-enters this interceptor
        // (after its .catch fires) starts fresh instead of seeing an
        // already-failed-but-still-locked refresh state. Symptom of
        // the old order: queued requests would 401 → enqueue → be
        // never drained because _isRefreshing was still true.
        const failed = _refreshQueue;
        _refreshQueue = [];
        _isRefreshing = false;
        failed.forEach(({ reject }) => reject(e));
        // Soft-redirect: tells AuthContext to drop the user; the
        // ProtectedRoute then renders the Login page. Skip the redirect
        // when the page is already login/public review surfaces.
        if (typeof window !== "undefined") {
          const p = window.location.pathname;
          const isPublic = p.startsWith("/login") || p.startsWith("/review") || p.startsWith("/wo-review");
          if (!isPublic) {
            window.dispatchEvent(new CustomEvent("auth:expired"));
          }
        }
        throw e;
      } finally {
        _isRefreshing = false;
      }
    }
    throw err;
  }
);

// Dashboard
export const getDashboardStats = () => API.get("/dashboard/stats");
// #62 — Team Live Status widget (manager-tier only; gated at /api/dashboard)
export const getTeamLiveStatus = () => API.get("/dashboard/team-live-status");
// iter148 — Lunch & GPS variance alerts (manager-tier only)
export const getFlaggedTimesheetAlerts = () => API.get("/timesheets/flagged-alerts");
export const resolveFlaggedTimesheet = (id) => API.post(`/timesheets/${id}/resolve-flag`);
export const getPriorityActions = () => API.get("/dashboard/priority-actions");
export const getLeadPipeline = () => API.get("/dashboard/lead-pipeline");
export const getProposalPipeline = () => API.get("/dashboard/proposal-pipeline");
export const getDashboardReminders = () => API.get("/dashboard/reminders");
export const getTopCustomers = () => API.get("/dashboard/top-customers");
export const getAnnualServiceDue = () => API.get("/dashboard/annual-service-due");
export const getScheduledWorkOrders = () => API.get("/dashboard/scheduled-work-orders");
export const getInspectionDeficiencies = (params) => API.get("/dashboard/inspection-deficiencies", { params });
export const createProposalFromDeficiencies = (data) => API.post("/proposals/from-deficiencies", data);
export const createWOFromDeficiencies = (data) => API.post("/work-orders/from-deficiencies", data);
// iter240 — Sales proposal follow-up emails
export const getProposalFollowupSettings = () => API.get("/proposal-followups/settings");
export const updateProposalFollowupSettings = (data) => API.put("/proposal-followups/settings", data);
export const getProposalFollowupDraft = (id) => API.get(`/proposal-followups/${id}/draft`);
export const toggleProposalFollowups = (id, paused) => API.patch(`/proposal-followups/${id}/toggle`, { paused });
export const markProposalFollowupSent = (id) => API.post(`/proposal-followups/${id}/mark-sent`);
export const processProposalFollowups = () => API.post("/proposal-followups/process");
// iter241 — unified customer communications timeline (History tab)
export const getClientCommunications = (clientId) => API.get(`/clients/${clientId}/communications`);
// iter242 — reply detection (auto-shutoff of proposal follow-ups)
export const getReplyDetectionStatus = () => API.get("/reply-detection/status");
export const runReplyScan = () => API.post("/reply-detection/scan");
// iter243 — reply alerts + follow-up activity log
export const getSalesAlerts = () => API.get("/sales-alerts");
export const dismissSalesAlert = (id) => API.post(`/sales-alerts/${id}/dismiss`);
export const dismissAllSalesAlerts = () => API.post("/sales-alerts/dismiss-all");
export const getProposalFollowupLog = (proposalId) => API.get("/proposal-followups/log", { params: { proposal_id: proposalId } });
export const getPaymentHolds = () => API.get("/dashboard/payment-holds");
export const getImmediateActions = () => API.get("/dashboard/immediate-actions");

// Clients
// limit=10000 = "all of them" for now. We have 252 today; an in-memory
// list of a few thousand still scrolls fine. Override per-call with
// `getClients({ limit: 50 })` for paginated surfaces.
export const getClients = (params) => API.get("/clients", { params: { limit: 10000, ...params } });
export const getClient = (id) => API.get(`/clients/${id}`);
export const createClient = (data) => API.post("/clients", data);
export const updateClient = (id, data) => API.put(`/clients/${id}`, data);
export const deleteClient = (id) => API.delete(`/clients/${id}`);
export const archiveClient = (id) => API.patch(`/clients/${id}/archive`);
export const restoreClient = (id) => API.patch(`/clients/${id}/restore`);
export const getArchivedClients = (params) => API.get("/clients", { params: { limit: 10000, ...params, archived: true } });
export const getClientStats = (id) => API.get(`/clients/${id}/stats`);
export const getClientDocuments = (id) => API.get(`/clients/${id}/documents`);
export const getClientFiles = (id) => API.get(`/clients/${id}/files`);
export const uploadClientFile = (id, formData) => API.post(`/clients/${id}/files`, formData, { headers: { "Content-Type": "multipart/form-data" } });
export const deleteClientFile = (id, fileId) => API.delete(`/clients/${id}/files/${fileId}`);
export const clientFileUrl = (id, fileId, inline = false) => `${BACKEND_URL}/api/clients/${id}/files/${fileId}/download${inline ? "?inline=true" : ""}`;
export const getLienDocs = (invoiceId) => API.get(`/invoices/${invoiceId}/lien-docs`);
export const uploadLienDoc = (invoiceId, formData) => API.post(`/invoices/${invoiceId}/lien-docs`, formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
export const deleteLienDoc = (invoiceId, docId) => API.delete(`/invoices/${invoiceId}/lien-docs/${docId}`);
export const lienDocUrl = (invoiceId, docId, inline = false) => `${BACKEND_URL}/api/invoices/${invoiceId}/lien-docs/${docId}/download${inline ? "?inline=true" : ""}`;

// Parts Orders
export const listPartsOrders = (status) => API.get(`/parts-orders${status ? `?status=${status}` : ""}`);
export const createPartsOrder = (data) => API.post("/parts-orders", data);
export const updatePartsOrder = (id, data) => API.patch(`/parts-orders/${id}`, data);
export const deletePartsOrder = (id) => API.delete(`/parts-orders/${id}`);
export const emailPartsQuote = (id) => API.post(`/parts-orders/${id}/email-quote`);
export const shipPartsOrder = (id, tracking = {}) => API.post(`/parts-orders/${id}/ship`, tracking);
export const markPartsOrderPaid = (id) => API.post(`/parts-orders/${id}/mark-paid`);
export const checkPartsPayment = (id) => API.post(`/parts-orders/${id}/check-payment`);
export const getPartsToShip = () => API.get("/dashboard/parts-to-ship");
export const exportPartsOrderToFoundation = (id) => API.post(`/parts-orders/${id}/foundation-export`);
export const previewPartsOrderPO = (id) => API.get(`/parts-orders/${id}/po-preview`);
export const createPartsOrderPO = (id) => API.post(`/parts-orders/${id}/create-po`);
export const logPartsOrderPOSent = (id, payload) => API.post(`/parts-orders/${id}/log-po-sent`, payload);
export const calculateTax = (data) => API.post("/calculate-tax", data);
export const checkClientNewLeadStatus = (id) => API.post(`/clients/${id}/check-new-lead`);
export const updateClientRating = (id, rating) => API.patch(`/clients/${id}/rating`, { rating });
export const updateClientFacilityType = (id, facility_type) => API.patch(`/clients/${id}/facility-type`, { facility_type });
export const updateSiteFacilityType = (id, facility_type) => API.patch(`/sites/${id}/facility-type`, { facility_type });

// Facility Types
export const getFacilityTypes = () => API.get("/facility-types");
export const createFacilityType = (data) => API.post("/facility-types", data);
export const deleteFacilityType = (id) => API.delete(`/facility-types/${id}`);

// Sites
export const getSites = (params) => API.get("/sites", { params: { limit: 10000, ...params } });
export const getSite = (id) => API.get(`/sites/${id}`);
export const createSite = (data) => API.post("/sites", data);
export const updateSite = (id, data) => API.put(`/sites/${id}`, data);
export const deleteSite = (id) => API.delete(`/sites/${id}`);

// Equipment
export const getEquipment = (params) => API.get("/equipment", { params: { limit: 10000, ...params } });
export const getEquipmentById = (id) => API.get(`/equipment/${id}`);
export const createEquipment = (data) => API.post("/equipment", data);
export const updateEquipment = (id, data) => API.put(`/equipment/${id}`, data);
export const deleteEquipment = (id) => API.delete(`/equipment/${id}`);
export const archiveEquipment = (id) => API.patch(`/equipment/${id}/archive`);
export const restoreEquipment = (id) => API.patch(`/equipment/${id}/restore`);
export const getArchivedEquipment = (params) => API.get("/equipment", { params: { ...params, archived: true } });

// Manufacturers (for equipment cascading dropdowns)
export const getManufacturers = () => API.get("/manufacturers");
export const createManufacturer = (data) => API.post("/manufacturers", data);
export const updateManufacturer = (id, data) => API.put(`/manufacturers/${id}`, data);
export const deleteManufacturer = (id) => API.delete(`/manufacturers/${id}`);

// Equipment Types (linked to manufacturer)
export const getEquipmentTypes = (manufacturerId) => 
  API.get("/equipment-types", { params: manufacturerId ? { manufacturer_id: manufacturerId } : {} });
export const createEquipmentType = (data) => API.post("/equipment-types", data);
export const deleteEquipmentType = (id) => API.delete(`/equipment-types/${id}`);

// Parts (linked to equipment type)
export const getParts = (equipmentTypeId) => 
  API.get("/parts", { params: equipmentTypeId ? { equipment_type_id: equipmentTypeId } : {} });
export const createPart = (data) => API.post("/parts", data);
export const deletePart = (id) => API.delete(`/parts/${id}`);

// Equipment Names (dropdown options)
export const getEquipmentNames = (clientId) => API.get("/equipment-names", { params: clientId ? { client_id: clientId } : {} });
export const createEquipmentName = (data) => API.post("/equipment-names", data);

// Leads
export const getLeads = (params) => API.get("/leads", { params: { limit: 10000, ...params } });
export const getLead = (id) => API.get(`/leads/${id}`);
export const createLead = (data) => API.post("/leads", data);
export const updateLead = (id, data) => API.put(`/leads/${id}`, data);
export const convertLead = (id, data) => API.post(`/leads/${id}/convert`, data);
// iter263 — lead workflow: mark-won for existing-customer leads + customer autocomplete.
export const markLeadWon = (id) => API.post(`/leads/${id}/mark-won`);
export const leadCustomerSearch = (q, limit = 8) => API.get("/leads/customer-search", { params: { q, limit } });
export const deleteLead = (id) => API.delete(`/leads/${id}`);
export const updateLeadStatus = (id, status, lost_reason) => API.patch(`/leads/${id}/status`, { status, ...(lost_reason !== undefined ? { lost_reason } : {}) });
export const updateLeadFollowUp = (id, data) => API.patch(`/leads/${id}/follow-up`, data);
export const updateLeadTemperature = (id, temperature) => API.patch(`/leads/${id}/temperature`, { temperature });
export const addLeadNote = (id, text) => API.post(`/leads/${id}/notes`, { text });
// iter343 — pipeline activities + convert-to-proposal
export const getLeadActivities = (id) => API.get(`/leads/${id}/activities`);
export const addLeadActivity = (id, data) => API.post(`/leads/${id}/activities`, data);
export const deleteLeadActivity = (id, activityId) => API.delete(`/leads/${id}/activities/${activityId}`);
export const convertLeadToProposal = (id, params) => API.post(`/leads/${id}/convert-to-proposal`, null, { params });
// iter343 — dashboard dismissals
export const getDashboardDismissals = (kind) => API.get("/dashboard/dismissals", { params: kind ? { kind } : {} });
export const addDashboardDismissal = (data) => API.post("/dashboard/dismissals", data);
export const removeDashboardDismissal = (kind, key) => API.delete(`/dashboard/dismissals/${kind}/${encodeURIComponent(key)}`);
export const getLeadFollowUps = () => API.get("/dashboard/lead-follow-ups");
export const getServiceTypes = () => API.get("/service-types");
export const createServiceType = (data) => API.post("/service-types", data);

export const getAccountManagers = () => API.get("/account-managers");
export const createAccountManager = (data) => API.post("/account-managers", data);
export const updateAccountManager = (id, data) => API.put(`/account-managers/${id}`, data);
export const deleteAccountManager = (id) => API.delete(`/account-managers/${id}`);
export const getProjectManagers = () => API.get("/project-managers");
export const createProjectManager = (data) => API.post("/project-managers", data);
export const updateProjectManager = (id, data) => API.put(`/project-managers/${id}`, data);
export const deleteProjectManager = (id) => API.delete(`/project-managers/${id}`);
export const getWOInvoices = (woId) => API.get(`/work-orders/${woId}/invoices`);
export const createInvoice = (woId, data) => API.post(`/work-orders/${woId}/invoices`, data);
export const updateInvoice = (id, data) => API.put(`/invoices/${id}`, data);
// iter306 — manual tax override (proposal | change_order | invoice | work_order)
export const setTaxOverride = (docType, docId, body) => API.put(`/tax-override/${docType}/${docId}`, body);
export const clearTaxOverride = (docType, docId, woId) => API.delete(`/tax-override/${docType}/${docId}`, { params: woId ? { wo_id: woId } : {} });
export const confirmTaxOverride = (docType, docId, woId) => API.post(`/tax-override/${docType}/${docId}/confirm`, null, { params: woId ? { wo_id: woId } : {} });
// iter310 — display-only hide-tax flag (never changes the tax math)
export const setHideTax = (docType, docId, hideTax, woId) => API.put(`/hide-tax/${docType}/${docId}`, { hide_tax: hideTax, wo_id: woId || null });
export const getAuditLog = (params) => API.get("/audit-log", { params });
export const getAuditLogFacets = () => API.get("/audit-log/facets");
// Status transitions go through the dedicated PATCH endpoint — the
// generic PUT deliberately drops `status` / `sent_at` / `paid_at`.
export const updateInvoiceStatus = (id, status) => API.patch(`/invoices/${id}/status`, { status });
export const deleteInvoice = (id) => API.delete(`/invoices/${id}`);
export const getUnpaidInvoices = () => API.get("/dashboard/unpaid-invoices");

// AR Reminder System
export const getARReminderSettings = () => API.get("/ar-reminder-settings");
export const updateARReminderSettings = (data) => API.put("/ar-reminder-settings", data);
export const getInvoiceReminders = (invoiceId) => API.get(`/invoices/${invoiceId}/reminders`);
export const getAllReminderLogs = (limit = 100) => API.get(`/ar-reminders/log?limit=${limit}`);
export const sendManualReminder = (invoiceId, data) => API.post(`/invoices/${invoiceId}/send-reminder`, data);
export const testSendARReminder = (data) => API.post(`/ar-reminders/test-send`, data);
export const getFailedEmails = () => API.get(`/dashboard/failed-emails`);
export const resolveFailedEmail = (logId, data = {}) => API.post(`/email-log/${logId}/resolve`, data);
export const retryFailedEmail = (logId, data) => API.post(`/email-log/${logId}/retry`, data);
export const getEmailStats = () => API.get(`/dashboard/email-stats`);
export const setEmailAutomation = (enabled) => API.put(`/email-settings/automation`, { enabled });
export const getEmailAutomationSwitches = () => API.get("/settings/email-automation");
export const saveEmailAutomationSwitches = (data) => API.put("/settings/email-automation", data);
export const sendTestEmail = (to) => API.post(`/email-settings/test-send`, { to });
export const getOutlookStatus = () => API.get("/outlook/status");
export const updateMyPreferences = (data) => API.patch("/auth/me/preferences", data);
export const translateBatch = (items, target) => API.post("/translate/batch", { items, target });
export const togglePauseReminders = (invoiceId, paused) => API.put(`/invoices/${invoiceId}/pause-reminders`, { paused });
export const processARReminders = () => API.post("/ar-reminders/process");

// AR Notes
export const getARNotes = (invoiceId) => API.get(`/invoices/${invoiceId}/ar-notes`);
export const addARNote = (invoiceId, data) => API.post(`/invoices/${invoiceId}/ar-notes`, data);

// Dashboard: Pending Invoices (auto-generated, need sending)
export const getPendingInvoices = () => API.get("/dashboard/pending-invoices");

// Dashboard: AR PM Alerts (invoices 35+ days past due needing PM attention)
export const getARPmAlerts = () => API.get("/dashboard/ar-pm-alerts");

// Foundation ERP Export

export const deleteServiceType = (id) => API.delete(`/service-types/${id}`);

// Lead Sources
export const getLeadSources = () => API.get("/lead-sources");
export const createLeadSource = (data) => API.post("/lead-sources", data);

// Jobs
export const getJobs = (params) => API.get("/jobs", { params: { limit: 10000, ...params } });
export const getJob = (id) => API.get(`/jobs/${id}`);
export const createJob = (data) => API.post("/jobs", data);
export const updateJob = (id, data) => API.put(`/jobs/${id}`, data);
export const deleteJob = (id) => API.delete(`/jobs/${id}`);

// Proposals
export const getProposals = (params) => API.get("/proposals", { params: { limit: 10000, ...params } });
export const getProposal = (id) => API.get(`/proposals/${id}`);
export const createProposal = (data) => API.post("/proposals", data);
export const updateProposal = (id, data) => API.put(`/proposals/${id}`, data);
export const importProposalLines = (formData) => API.post("/proposals/import-lines", formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
export const deleteProposal = (id) => API.delete(`/proposals/${id}`);
export const hardDeleteProposal = (id) => API.delete(`/proposals/${id}/hard`);
export const convertProposalToWO = (id, opts = {}) => API.post(`/proposals/${id}/convert-to-wo`, { foundation_sync_mode: opts.foundation_sync_mode || "skipped" });
// iter255 — mark-won now auto-creates a Work Order and takes a
// `foundation_sync_mode` ("skipped" | "queued", default skipped). Set
// create_wo=false to keep the legacy no-WO branch (rare).
export const markProposalWon = (id, opts = {}) =>
  API.post(`/proposals/${id}/mark-won`, {
    foundation_sync_mode: opts.foundation_sync_mode || "skipped",
    create_wo: opts.create_wo !== false,
  });
export const backfillWonOrphans = (opts = {}) =>
  API.post(`/proposals/backfill-won-orphans`, {
    foundation_sync_mode: opts.foundation_sync_mode || "skipped",
    dry_run: !!opts.dry_run,
  });
export const updateProposalPipeline = (id, data) => API.patch(`/proposals/${id}/pipeline`, data);
export const bulkUpdateProposalsPipeline = (data) => API.post(`/proposals/bulk-pipeline`, data);
export const addClientContact = (clientId, data, siteId) => API.post(`/clients/${clientId}/contacts${siteId ? `?site_id=${encodeURIComponent(siteId)}` : ""}`, data);
export const updateClientContact = (clientId, contactId, data) => API.patch(`/clients/${clientId}/contacts/${contactId}`, data);
export const getBillOfMaterials = (woId) => API.get(`/work-orders/${woId}/bill-of-materials`);
export const setProcurementItemVendor = (woId, itemId, manufacturer_name) =>
  API.patch(`/work-orders/${woId}/procurement-items/${itemId}/vendor`, { manufacturer_name });
export const setProcurementItemFromStock = (woId, itemId, from_stock) =>
  API.patch(`/work-orders/${woId}/procurement-items/${itemId}/from-stock`, { from_stock });
export const getProposalNextActions = () => API.get("/dashboard/proposal-next-actions");

// Communications
export const getCommunications = (params) => API.get("/communications", { params });
export const getCommunication = (id) => API.get(`/communications/${id}`);
export const createCommunication = (data) => API.post("/communications", data);
export const deleteCommunication = (id) => API.delete(`/communications/${id}`);

// Service History
export const getServiceHistory = (params) => API.get("/service-history", { params });

// Work Orders
export const getWorkOrders = (params) => API.get("/work-orders", { params: { limit: 10000, ...params } });
export const getWorkOrdersGlobal = (params) => API.get("/work-orders-global", { params: { limit: 10000, ...params } });

// Office note log on a work order (iter304) — running timestamped entries,
// office only, author-only edits, no deletes.
export const listWONoteLog = (woId) => API.get(`/work-orders/${woId}/note-log`);
export const getWOActivity = (woId) => API.get(`/work-orders/${woId}/activity`);
export const addWONote = (woId, text) => API.post(`/work-orders/${woId}/note-log`, { text });
export const editWONote = (woId, noteId, text) => API.patch(`/work-orders/${woId}/note-log/${noteId}`, { text });
export const getSearchIndex = (scope) => API.get("/search-index", scope ? { params: { scope } } : undefined);
export const searchGlobal = (q, scope) => API.get("/search", { params: scope ? { q, scope } : { q } });
export const getNextWONumber = (clientId) => API.get(`/work-orders/next-number/${clientId}`);
export const getWorkOrder = (id) => API.get(`/work-orders/${id}`);
export const createWorkOrder = (data) => API.post("/work-orders", data);
export const updateWorkOrder = (id, data) => API.put(`/work-orders/${id}`, data);
export const renumberWorkOrder = (id, woNumber) => API.patch(`/work-orders/${id}/number`, { wo_number: woNumber });
// iter220 — status transitions go through the dedicated PATCH endpoint
// (the generic PUT deliberately drops `status`). Validates the
// transition graph and fires side effects (inventory, dispatch, invoice).
export const patchWorkOrderStatus = (id, status, cancel) =>
  API.patch(`/work-orders/${id}/status`, {
    status,
    ...(cancel?.code ? { cancellation_reason_code: cancel.code } : {}),
    ...(cancel?.detail ? { cancellation_reason: cancel.detail } : {}),
  });
// iter271 — dispatch notes read receipt (tech opened the notes panel)
export const markDispatchNotesSeen = (dispatchId, body = {}) =>
  API.post(`/dispatches/${dispatchId}/notes/seen`, body);
export const updateWOLiftNote = (id, lift_note) => API.patch(`/work-orders/${id}/lift-note`, { lift_note });
export const getRecentProposalApprovals = () => API.get("/proposals/recent-approvals");
export const acknowledgeProposalApproval = (id) => API.post(`/proposals/${id}/acknowledge-approval`);
export const signWorkOrder = (id, data) => API.patch(`/work-orders/${id}/sign`, data);
export const completeWorkOrder = (id) => API.patch(`/work-orders/${id}/status`, { status: "completed" });
export const uploadExecutedWO = (id, formData) => API.post(`/work-orders/${id}/upload-executed`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getExecutedWODocument = (id) => `${API.defaults.baseURL}/work-orders/${id}/executed-document`;

// WO Tech Info docs
export const uploadWOTechDoc = (woId, formData) => API.post(`/work-orders/${woId}/tech-docs`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteWOTechDoc = (woId, docId) => API.delete(`/work-orders/${woId}/tech-docs/${docId}`);
export const getWOTechDocUrl = (woId, docId) => `${API.defaults.baseURL}/work-orders/${woId}/tech-docs/${docId}`;
export const getWOTechInfo = (woId) => API.get(`/work-orders/${woId}/tech-info`);

// Master Part Tech Docs
export const uploadPartDoc = (partId, formData) => API.post(`/master-parts/${partId}/docs`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deletePartDoc = (partId, docId) => API.delete(`/master-parts/${partId}/docs/${docId}`);
export const getPartDocUrl = (partId, docId) => `${API.defaults.baseURL}/master-parts/${partId}/docs/${docId}`;
export const uploadExecutedProposal = (id, formData) => API.post(`/proposals/${id}/upload-executed`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getExecutedProposalDocument = (id) => `${API.defaults.baseURL}/proposals/${id}/executed-document`;

// Square Payments
export const createPaymentLink = (invoiceId) => API.post("/payments/create-link", { invoice_id: invoiceId });
export const getInvoicePaymentInfo = (invoiceId) => API.get(`/payments/invoice/${invoiceId}`);

// Public Review & Sign
export const generateProposalReviewToken = (id) => API.post(`/proposals/${id}/generate-review-token`);
export const getProposalReview = (token) => API.get(`/proposal-review/${token}`);
export const signProposal = (token, data) => API.post(`/proposal-review/${token}/sign`, data, { timeout: 60000 });
export const generateWOReviewToken = (id) => API.post(`/work-orders/${id}/generate-review-token`);
export const ensureWOShortCode = (id) => API.post(`/work-orders/${id}/ensure-short-code`);
export const getWOReview = (token) => API.get(`/wo-review/${token}`);
export const signWO = (token, data) => API.post(`/wo-review/${token}/sign`, data);
export const deleteWorkOrder = (id) => API.delete(`/work-orders/${id}`);
export const submitChangeOrder = (woId, coId) => API.post(`/work-orders/${woId}/change-orders/${coId}/submit`);
export const revertCOToDraft = (woId, coId) => API.post(`/work-orders/${woId}/change-orders/${coId}/revert-to-draft`);
export const approveChangeOrder = (woId, coId, co) => API.post(`/work-orders/${woId}/change-orders/${coId}/approve`, co ? { co } : {});
export const retryCOFoundationSync = (woId, coId) => API.post(`/work-orders/${woId}/change-orders/${coId}/foundation-sync/retry`);
// SVC-FIX-2 — PO approval step
export const submitPOForApproval = (woId, poId) => API.post(`/work-orders/${woId}/purchase-orders/${poId}/submit-for-approval`);
export const approvePO = (woId, poId) => API.post(`/work-orders/${woId}/purchase-orders/${poId}/approve`);
export const rejectPO = (woId, poId, reason) => API.post(`/work-orders/${woId}/purchase-orders/${poId}/reject`, { reason });
export const retryPOFoundationSync = (woId, poId) => API.post(`/work-orders/${woId}/purchase-orders/${poId}/foundation-sync/retry`);
export const getApprovalQueue = (includeDrafts) => API.get("/approvals/queue", { params: { include_drafts: !!includeDrafts } });
// SVC-FIX-3 — direct-cost pull status / run
export const getFoundationActualsStatus = () => API.get("/integrations/foundation/actuals/status");
export const pullFoundationActualsAll = () => API.post("/integrations/foundation/actuals/pull");
export const getFoundationSyncQueue = () => API.get("/integrations/foundation/sync-queue");
export const getFoundationBridgeAlerts = () => API.get("/integrations/foundation/bridge-alerts");
export const unapproveChangeOrder = (woId, coId) => API.post(`/work-orders/${woId}/change-orders/${coId}/unapprove`);
export const unlockChangeOrder = (woId, coId) => API.post(`/work-orders/${woId}/change-orders/${coId}/foundation-unlock`, { confirm_purged: true });
export const poUpdatedInFoundation = (woId, poId) => API.post(`/work-orders/${woId}/purchase-orders/${poId}/foundation-sync/updated-in-foundation`);
export const searchFoundationJobs = (q) => API.get("/integrations/foundation/jobs/search", { params: { q } });
export const linkFoundationWoQueue = (woId, jobNo, allowInactive = false) => API.post(`/integrations/foundation/wo-queue/${woId}/link`, { job_no: jobNo, allow_inactive: !!allowInactive });
export const notApproveChangeOrder = (woId, coId) => API.post(`/work-orders/${woId}/change-orders/${coId}/not-approve`);
export const listChangeOrders = (params = {}) => API.get("/change-orders", { params });
export const getCOReview = (token) => API.get(`/co-review/${token}`);
export const respondToCO = (token, data) => API.post(`/co-review/${token}/respond`, data);
// Purchase Orders
export const getPurchaseOrders = (woId) => API.get(`/work-orders/${woId}/purchase-orders`);
export const getPurchaseOrder = (woId, poId) => API.get(`/work-orders/${woId}/purchase-orders/${poId}`);
export const createPurchaseOrder = (woId, data) => API.post(`/work-orders/${woId}/purchase-orders`, data);
export const updatePurchaseOrder = (woId, poId, data) => API.put(`/work-orders/${woId}/purchase-orders/${poId}`, data);
export const acknowledgePurchaseOrder = (woId, poId, payload) => API.patch(`/work-orders/${woId}/purchase-orders/${poId}/acknowledge`, payload);
export const deletePurchaseOrder = (woId, poId) => API.delete(`/work-orders/${woId}/purchase-orders/${poId}`);
export const updatePOStatus = (woId, poId, status) => API.patch(`/work-orders/${woId}/purchase-orders/${poId}/status`, { status });
export const receivePOItems = (woId, poId, itemIds) => API.patch(`/work-orders/${woId}/purchase-orders/${poId}/receive-items`, { item_ids: itemIds });
export const generatePOFromBOM = (woId, mfrName) => API.post(`/work-orders/${woId}/purchase-orders/generate`, { manufacturer_name: mfrName });
export const generateAllPOs = (woId) => API.post(`/work-orders/${woId}/purchase-orders/generate-all`);
export const parseVendorQuote = (woId, file) => { const fd = new FormData(); fd.append("file", file); return API.post(`/work-orders/${woId}/purchase-orders/parse-quote`, fd); };
// Settings
export const getSettings = () => API.get("/settings");
export const updateSettings = (data) => API.put("/settings", data);
export const lookupTaxRate = (params) => API.get("/tax-lookup", { params });
export const getTaxNumbers = () => API.get("/tax-numbers");
// Technicians
export const getTechnicians = (opts = {}) => API.get("/technicians", { params: opts.includeArchived ? { include_archived: true } : undefined });
export const createTechnician = (data) => API.post("/technicians", data);
export const updateTechnician = (id, data) => API.put(`/technicians/${id}`, data);
export const deleteTechnician = (id) => API.delete(`/technicians/${id}`);
// Dispatch
export const getDispatchWorkOrders = (params) => API.get("/dispatch/work-orders", { params });
export const dispatchAssign = (data) => API.patch("/dispatch/assign", data);
export const getDispatchQueue = () => API.get("/dispatch/queue");
export const getDispatchApprovedQueue = () => API.get("/dispatch/approved-queue");
export const createDispatch = (data) => API.post("/dispatches", data);
export const listDispatches = (params) => API.get("/dispatches", { params });
export const deleteDispatch = (id) => API.delete(`/dispatches/${id}`);
export const updateDispatchStatus = (id, data) => API.post(`/dispatches/${id}/update-status`, data);
export const duplicateDispatch = (id, data) => API.post(`/dispatches/${id}/duplicate`, data);
export const updateDispatch = (id, data) => API.put(`/dispatches/${id}`, data);
export const reorderDispatches = (order) => API.patch("/dispatches/reorder", { order });
// Contract jobs — Contract-division projects; dispatch + timesheets only (no WO / Foundation / invoicing)
export const listContractJobs = (params) => API.get("/contract-jobs", { params });
export const createContractJob = (data) => API.post("/contract-jobs", data);
export const updateContractJob = (id, data) => API.put(`/contract-jobs/${id}`, data);
export const archiveContractJob = (id) => API.delete(`/contract-jobs/${id}`);
export const reopenContractJob = (id) => API.post(`/contract-jobs/${id}/reopen`);
export const addContractFieldNote = (id, data) => API.post(`/contract-jobs/${id}/field-notes`, data);
// iter396 — Tech Lookup mirror + shared Field Access Notes
export const lookupSearch = (q) => API.get("/field-lookup/search", { params: { q } });
export const lookupClient = (id) => API.get(`/field-lookup/clients/${id}`);
export const lookupSite = (id) => API.get(`/field-lookup/sites/${id}`);
export const lookupWorkOrder = (id) => API.get(`/field-lookup/work-orders/${id}`);
export const getFieldMap = () => API.get("/field-lookup/map");
export const listAccessNotes = (target, targetId) => API.get("/field-lookup/access-notes", { params: { target, target_id: targetId } });
export const addAccessNote = (data) => API.post("/field-lookup/access-notes", data);
export const editAccessNote = (id, data) => API.patch(`/field-lookup/access-notes/${id}`, data);
export const deleteAccessNote = (id, data) => API.post(`/field-lookup/access-notes/${id}/delete`, data);
export const appendTodayTimesheetNote = (techId, data) => API.post(`/timesheets/today/${techId}/append-note`, data);
export const getTechWorkOrders = (techId, params) => API.get(`/dispatch/tech-work-orders/${techId}`, { params });
export const updateWOStatus = (data) => API.post("/dispatch/update-status", data);
export const updateTechLocation = (data) => API.post("/dispatch/tech-location", data);
export const getGpsWindow = (techId) => API.get(`/dispatch/gps-window/${techId}`);
export const toggleTechGps = (data) => API.post("/dispatch/tech-gps-toggle", data);
export const getMapData = (params) => API.get("/dispatch/map-data", { params });
export const cleanupWOCompletionNote = (woId, rawText) => API.post(`/work-orders/${woId}/cleanup-note`, { raw_text: rawText });
export const completeWorkOrderFull = (woId, payload) => API.post(`/work-orders/${woId}/complete`, payload);
export const getLiveTechs = () => API.get("/dispatch/live-techs");
export const getSmartRoute = (data) => API.post("/dispatch/smart-route", data);
export const geocodeAddress = (data) => API.post("/geocode", data);
// ── Timesheets ─────────────────────────────────────────────
export const getTodaysTimesheet = (techId) => API.get(`/timesheets/today/${techId}`);
export const clockInToday = (techId) => API.post(`/timesheets/today/${techId}/clock-in`);
export const clockOutToday = (techId) => API.post(`/timesheets/today/${techId}/clock-out`);
export const getTimesheetDayReview = (date) => API.get("/timesheets/day-review", { params: { date } });
export const submitTechEntry = (tsId, payload) => API.post(`/timesheets/${tsId}/tech-entry`, payload);
export const listTimesheets = (params) => API.get("/timesheets", { params });
export const approveTimesheet = (tsId, payload) => API.post(`/timesheets/${tsId}/approve`, payload);
export const getPendingApprovalCount = () => API.get("/timesheets/pending-approval/count");
export const runVarianceReview = () => API.post("/timesheets/run-variance-review");
export const getSmartSuggest = (woId, params) => API.get(`/dispatch/smart-suggest/${woId}`, { params });
export const autoScheduleDispatches = (data) => API.post("/dispatch/auto-schedule", data);
export const undoAutoSchedule = (data) => API.post("/dispatch/auto-schedule/undo", data);
export const getSignoffSummary = (woId) => API.get(`/work-orders/${woId}/signoff-summary`);
export const customerSignoff = (woId, data) => API.post(`/work-orders/${woId}/customer-signoff`, data);
export const restoreTechnician = (id) => API.patch(`/technicians/${id}/restore`);
export const permanentlyDeleteTechnician = (id) => API.delete(`/technicians/${id}/hard`);
// Field Notes
export const addFieldNote = (woId, data) => API.post(`/work-orders/${woId}/field-notes`, data);
export const addDispatchCardMaterial = (woId, data) => API.post(`/work-orders/${woId}/dispatch-card-materials`, data);
export const removeDispatchCardMaterial = (woId, materialId) => API.delete(`/work-orders/${woId}/dispatch-card-materials/${materialId}`);
export const checkCardProgress = (woId, data) => API.post(`/work-orders/${woId}/card-progress/check`, data);
export const uncheckCardProgress = (woId, data) => API.post(`/work-orders/${woId}/card-progress/uncheck`, data);
export const checkAllCardProgress = (woId, data) => API.post(`/work-orders/${woId}/card-progress/check-all`, data);
export const addDispatchNote = (dispatchId, text, createdBy) => API.post(`/dispatches/${dispatchId}/notes`, { text, created_by: createdBy });
export const getFieldNotes = (woId) => API.get(`/work-orders/${woId}/field-notes`);
// ZIP Lookup
export const lookupZip = (city, state) => API.get("/lookup-zip", { params: { city, state } });
// Master Equipment Library
export const getMasterScopes = () => API.get("/master-scopes");
export const createMasterScope = (data) => API.post("/master-scopes", data);
export const updateMasterScope = (id, data) => API.put(`/master-scopes/${id}`, data);
export const deleteMasterScope = (id) => API.delete(`/master-scopes/${id}`);
export const getMasterEquipment = (params) => API.get("/master-equipment", { params });
export const createMasterEquipment = (data) => API.post("/master-equipment", data);
export const updateMasterEquipment = (id, data) => API.put(`/master-equipment/${id}`, data);
export const deleteMasterEquipment = (id) => API.delete(`/master-equipment/${id}`);
export const getMasterParts = (params) => API.get("/master-parts", { params });
export const createMasterPart = (data) => API.post("/master-parts", data);
export const updateMasterPart = (id, data) => API.put(`/master-parts/${id}`, data);
export const deleteMasterPart = (id) => API.delete(`/master-parts/${id}`);
export const getPartInventoryLog = (partId, limit = 20) => API.get(`/master-parts/${partId}/inventory-log`, { params: { limit } });
// Window Care — subcontract rate sheet defaults (library + settings)
export const getWindowCareDefaults = () => API.get("/window-care/defaults");
export const setWindowCareDefaults = (data) => API.put("/window-care/defaults", data);
// Per-site saved pane counts ("this building's pane counts")
export const getWindowCareTemplate = (siteId) => API.get(`/window-care/template/${siteId}`);
export const saveWindowCareTemplate = (siteId, data) => API.put(`/window-care/template/${siteId}`, data);
export const bulkImportParts = (payload) => API.post("/master-parts/bulk-import", payload);
export const searchMasterEquipment = (q) => API.get("/master-equipment-search", { params: { q } });

// Inspection Templates
export const getInspectionTemplates = () => API.get("/inspection-templates");export const getInspectionTemplate = (id) => API.get(`/inspection-templates/${id}`);
export const createInspectionTemplate = (data) => API.post("/inspection-templates", data);
export const updateInspectionTemplate = (id, data) => API.put(`/inspection-templates/${id}`, data);
export const deleteInspectionTemplate = (id) => API.delete(`/inspection-templates/${id}`);
export const copyInspectionTemplate = (id) => API.post(`/inspection-templates/${id}/copy`);

// Active Inspections (Conducting)
export const getInspections = (params) => API.get("/inspections", { params: { limit: 10000, ...params } });
export const getInspection = (id) => API.get(`/inspections/${id}`);
export const createInspection = (data) => API.post("/inspections", data);
export const createInspectionFromTemplate = (templateId, woId, dispatchId) =>
  API.post(`/inspections/from-template/${templateId}`, { work_order_id: woId, dispatch_id: dispatchId });
export const updateInspection = (id, data) => API.put(`/inspections/${id}`, data);
export const updateInspectionSections = (id, data) => API.patch(`/inspections/${id}/sections`, data);
export const deleteInspection = (id) => API.delete(`/inspections/${id}`);
// Technician offline support
export const getOfflineBundle = (days = 14) => API.get("/tech/offline-bundle", { params: { days } });
export const syncTechInspections = (items) => API.post("/tech/inspections/sync", { items });
export const getInspectionReviewQueue = () => API.get("/tech/inspections/review-queue");
export const emailInspection = (id, payload) => API.post(`/inspections/${id}/email`, payload);
export const getInspectionsToSend = () => API.get("/dashboard/inspections-to-send");
export const completeInspection = (id) => API.patch(`/inspections/${id}/complete`);
export const uploadInspectionPhoto = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return API.post("/inspections/upload-photo", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// PDF Downloads (use downloadPdfUrl from downloadBlob.js for direct browser downloads)
// These blob-based functions are kept for programmatic access if needed:
export const downloadInvoicePdf = (invoiceId) => API.get(`/invoices/${invoiceId}/pdf`, { responseType: "blob" });
export const downloadInspectionPdf = (inspectionId) => API.get(`/inspections/${inspectionId}/pdf`, { responseType: "blob" });
export const downloadProposalPdf = (proposalId) => API.get(`/proposals/${proposalId}/pdf`, { responseType: "blob" });

// Fillable-PDF fallback for inspections (iter303) — export a form a tech
// can fill offline, then import the returned file back onto the record.
export const importInspectionPdf = (inspectionId, file, markComplete = false) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("mark_complete", markComplete ? "true" : "false");
  return API.post(`/inspections/${inspectionId}/import-pdf`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Admin / Sandbox
export const wipeTestData = (confirm) => API.post("/admin/wipe-test-data", { confirm });
export const createSandbox = () => API.post("/admin/create-sandbox");
export const resetSandbox = () => API.post("/admin/reset-sandbox");

// Drive-time compensation
export const getDriveTimeForSite = (siteId, roundTrip = true) => API.get(`/drive-time/${siteId}`, { params: { round_trip: roundTrip } });

// ── Miter Payroll Integration ──────────────────────────────────────────
// Manager-run weekly push of reconciled timesheets. Until MITER_API_KEY +
// MITER_BASE_URL are populated the backend stays in dry-run mode; previews
// and logs still work so the flow can be exercised end-to-end.
export const getMiterConfigStatus = () => API.get("/miter/config-status");
export const getMiterEmployeesNeedingMapping = () => API.get("/miter/employees-needing-mapping");
export const getMiterMappingStatus = () => API.get("/miter/mapping-status");
export const getMiterRemoteTeamMembers = () => API.get("/miter/remote-team-members");
export const mapMiterTechnician = (payload) => API.put("/miter/map-technician", payload);
// iter194 — Self-healing per-tech Miter resync. Hits the live Miter
// `/api/v2/team_members` endpoint, matches by email, and stamps the
// canonical 24-char hex miter_employee_id. Used by the Re-sync
// button on each row of the Settings → Miter mapping panel.
export const resyncMiterTechnician = (techId) => API.post(`/miter/technicians/${techId}/resync-from-miter`);
export const getMiterBatchPreview = (weekStart) => API.get("/miter/batch-preview", { params: { week_start: weekStart } });
export const pushMiterBatch = (weekStart, force = false) => API.post("/miter/push-batch", { week_start: weekStart, force });
export const getMiterPushLog = (weekStart, runId) => API.get("/miter/push-log", { params: { week_start: weekStart, run_id: runId } });
// iter189 — inline clock-time editor on the Miter Payroll Panel.
export const patchMiterTimesheetClockTimes = (timesheetId, body) =>
  API.patch(`/miter/timesheets/${timesheetId}/clock-times`, body);
// #51 — Miter Jobs (Settings → Miter → Jobs tab)
export const getMiterJobs = () => API.get("/miter/jobs");
export const syncMiterJobs = () => API.post("/miter/jobs/sync");
export const upsertMiterJobMapping = (payload) => API.put("/miter/jobs/map", payload);
export const getMiterJobMappings = () => API.get("/miter/jobs/mappings");
export const getMiterMockInbox = () => API.get("/_mock/miter/inbox");
export const clearMiterMockInbox = () => API.delete("/_mock/miter/inbox");

// iter155 / iter156 — Foundation push-log manager-queue + SLA alert
export const getFoundationPushLog = (params) =>
  API.get("/integrations/foundation/push-log", { params: params || {} });
export const approveFoundationPushLog = (id) =>
  API.post(`/integrations/foundation/push-log/${id}/approve`);
export const rejectFoundationPushLog = (id, reason) =>
  API.post(`/integrations/foundation/push-log/${id}/reject`, { reason: reason || "" });
export const getFoundationSlaAlerts = () =>
  API.get("/integrations/foundation/sla-alerts");

// iter164 — Foundation bridge ops + cross-reference surfaces
export const getFoundationBridgeHealth = () =>
  API.get("/integrations/foundation/bridge-health");
// iter214 — Bridge Health widget (heartbeat + per-doc-type lag)
export const getFoundationBridgeTelemetry = () =>
  API.get("/integrations/foundation/bridge-telemetry");
// iter218 — on-demand live schema check (table/column existence probe)
export const getFoundationSchemaCheck = () =>
  API.get("/integrations/foundation/schema-check");
export const dispatchFoundationBridge = (limit = 25) =>
  API.post("/integrations/foundation/bridge-dispatch", { limit });
export const getFoundationSyncStatus = () =>
  API.get("/integrations/foundation/sync-status");
export const syncFoundationTables = (tables) =>
  API.post("/integrations/foundation/sync", tables ? { tables } : {});
export const getFoundationApprovalSettings = () =>
  API.get("/integrations/foundation/approval-settings");
export const updateFoundationApprovalSettings = (settings) =>
  API.put("/integrations/foundation/approval-settings", { settings });
export const getFoundationProjectTypeMap = () =>
  API.get("/integrations/foundation/project-type-map");
export const updateFoundationProjectTypeMap = (map) =>
  API.put("/integrations/foundation/project-type-map", { map });
// T&M charge log (project_type == "tm" work orders)
export const addTMCharge = (woId, data) => API.post(`/work-orders/${woId}/tm-charges`, data);
export const updateTMCharge = (woId, chargeId, data) => API.put(`/work-orders/${woId}/tm-charges/${chargeId}`, data);
export const deleteTMCharge = (woId, chargeId) => API.delete(`/work-orders/${woId}/tm-charges/${chargeId}`);
export const generateTMInvoice = (woId, chargeIds) => API.post(`/work-orders/${woId}/tm-charges/generate-invoice`, { charge_ids: chargeIds });
// Per-doc-type push triggers
export const pushChangeOrderToFoundation = (woId, coId) =>
  API.post(`/work-orders/${woId}/change-orders/${coId}/push-to-foundation`);
// Foundation AR invoice push (Phase 3) — spec-compliant ar_invoice + ar_invoice_gl
export const getInvoiceFoundationARPreview = (invoiceId) =>
  API.get(`/invoices/${invoiceId}/foundation-ar/preview`);
export const pushInvoiceFoundationAR = (invoiceId) =>
  API.post(`/invoices/${invoiceId}/foundation-ar/push`);
export const getFoundationARStatus = () => API.get("/integrations/foundation/ar/status");
export const foundationARDryRun = (invoiceId) => API.post(`/invoices/${invoiceId}/foundation-ar/dry-run`);
export const getFoundationARQueue = (params) => API.get("/integrations/foundation/ar-queue", { params });
export const getFoundationPOReadback = (job_no) => API.get("/integrations/foundation/po-sync/readback", { params: { job_no } });
// CO / PO staging sync (pending_po_sync_* / job_chg_phq) — preview → dry run (always rolled back) → insert now
const _fsync = (woId, kind, docId, tail) => `/work-orders/${woId}/foundation-sync/${kind}/${docId}/${tail}`;
export const foundationSyncPreview = (woId, kind, docId) => API.get(_fsync(woId, kind, docId, "preview"));
export const foundationSyncDryRun = (woId, kind, docId) => API.post(_fsync(woId, kind, docId, "dry-run"));
export const foundationSyncInsertNow = (woId, kind, docId, confirm) => API.post(_fsync(woId, kind, docId, "insert-now"), { confirm });
export const foundationSyncSend = (woId, kind, docId) => API.post(_fsync(woId, kind, docId, "send"));
export const foundationSyncCheck = (woId, kind, docId) => API.post(_fsync(woId, kind, docId, "check"));
export const foundationSyncFixVendor = (woId, poId, foundationVendorNo) =>
  API.post(_fsync(woId, "purchase_order", poId, "fix-vendor"), { foundation_vendor_no: foundationVendorNo });
export const getFoundationSyncDocs = () => API.get("/integrations/foundation/sync-docs");
export const foundationSyncDryRuns = (woId, kind, docId, payloadHash) => API.get(_fsync(woId, kind, docId, "dry-runs"), { params: payloadHash ? { payload_hash: payloadHash } : {} });
export const reportFoundationSyncError = (woId, kind, docId, text) => API.post(_fsync(woId, kind, docId, "report-error"), { text });
// iter312 — diagnosis + backlog operations
export const getFoundationARTriggers = () => API.get("/integrations/foundation/ar/triggers");
export const getFoundationARSupportRequest = (invoiceId) => API.get(`/invoices/${invoiceId}/foundation-ar/support-request`);
export const getFoundationARTaxAudit = (invoiceId) => API.get(`/invoices/${invoiceId}/foundation-ar/tax-audit`);
export const flagFoundationARTax = (invoiceId, comment = "") => API.post(`/invoices/${invoiceId}/foundation-ar/flag-tax`, { comment });
export const startFoundationARReplay = (body = {}) => API.post("/integrations/foundation/ar/replay", body);
export const getFoundationARReplayRuns = (limit = 10) => API.get("/integrations/foundation/ar/replay", { params: { limit } });
export const getFoundationARReplayRun = (runId) => API.get(`/integrations/foundation/ar/replay/${runId}`);
export const reconcileFoundationAR = (invoiceIds = null) => API.post("/integrations/foundation/ar/reconcile", { invoice_ids: invoiceIds });
export const getFoundationSchemaTables = () => API.get("/integrations/foundation/schema");
export const getFoundationSchemaSpec = (table) => API.get(`/integrations/foundation/schema/${table}`);
// iter314 — Foundation budget import CSV (download only, no bridge)
export const getFoundationBudgetRegister = (params) => API.get("/integrations/foundation/budget-register", { params });
export const setFoundationJobTypeBudget = (body) => API.post("/integrations/foundation/job-type-budget", body);
export const downloadFoundationBudgetCsv = (woIds, context = null) =>
  API.post("/integrations/foundation/budget-export", { wo_ids: woIds, context }, { responseType: "blob" });
export const getFoundationBudgetExportLog = (limit = 20) => API.get("/integrations/foundation/budget-export/log", { params: { limit } });
export const getFoundationARLog = (limit = 50) =>
  API.get("/integrations/foundation/ar/log", { params: { limit } });
// Client cross-reference: Foundation dispatch history
export const getClientFoundationDispatches = (clientId, limit = 50) =>
  API.get(`/clients/${clientId}/foundation-dispatches`, { params: { limit } });

// ── iter165 — Central Purchasing Inbox + M365 OAuth ────────────────────
// Inbox table + actions
export const listPurchasingInbox = (params = {}) =>
  API.get("/purchasing-inbox", { params });
export const getPurchasingInboxItem = (id) =>
  API.get(`/purchasing-inbox/${id}`);
export const ingestPurchasingInboxManual = (payload) =>
  API.post("/purchasing-inbox/ingest-manual", payload);
export const ingestPurchasingInboxEml = (file) => {
  const form = new FormData();
  form.append("file", file);
  return API.post("/purchasing-inbox/ingest-eml", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const reprocessPurchasingInbox = (id) =>
  API.post(`/purchasing-inbox/${id}/reprocess`);
export const dismissPurchasingInbox = (id) =>
  API.post(`/purchasing-inbox/${id}/dismiss`);
export const bulkPurchasingInboxAction = (inboxIds, action) =>
  API.post("/purchasing-inbox/bulk-action", { inbox_ids: inboxIds, action });

// M365 OAuth
export const getM365Status = () => API.get("/m365/status");
export const getM365AuthUrl = () => API.get("/m365/auth-url");
export const disconnectM365 = () => API.post("/m365/disconnect");
export const pollM365Inbox = (top = 25) =>
  API.post("/m365/poll-inbox", null, { params: { top } });

// iter166 — M365 webhook subscription management
export const getM365Subscription = () => API.get("/m365/subscription");
export const createM365Subscription = () => API.post("/m365/subscription");
export const deleteM365Subscription = () => API.delete("/m365/subscription");
export const renewAllM365Subscriptions = () =>
  API.post("/m365/subscription/renew-all");

// iter166 — Per-user GPT ingest quota (read-only)
export const getPurchasingInboxQuota = () =>
  API.get("/purchasing-inbox/quota");

// Central PO dashboard
export const listPurchasingPOs = (filters = {}) =>
  API.get("/purchasing/pos", { params: filters });
export const bulkPOAction = (poIds, action, body = {}) =>
  API.post("/purchasing/pos/bulk-action", { po_ids: poIds, action, body });

// Unified receiving — vendor email review + shipment tracking
export const getPurchasingInboxSummary = () => API.get("/purchasing-inbox/summary");
export const getPurchasingInboxSettings = () => API.get("/purchasing-inbox/settings");
export const putPurchasingInboxSettings = (inbound_address) => API.put("/purchasing-inbox/settings", { inbound_address });
export const applyInboxToPO = (inboxId, poId) => API.post(`/purchasing-inbox/${inboxId}/apply-to-po`, { po_id: poId });
export const patchPOShipment = (poId, payload) => API.patch(`/purchasing/pos/${poId}/shipment`, payload);
export const patchPOStatus = (poId, status) => API.patch(`/purchasing/pos/${poId}/status`, { status });
export const getWOPOCommitments = (woId) => API.get(`/work-orders/${woId}/po-commitments`);
export const addInvoiceARNote = (invoiceId, text, by = "") => API.post(`/invoices/${invoiceId}/ar-notes`, { note: text, created_by: by });
export const getLienReminderDraft = (invoiceId) => API.get(`/invoices/${invoiceId}/lien-reminder`);
export const sendLienReminder = (invoiceId, data) => API.post(`/invoices/${invoiceId}/lien-reminder`, data);
export const getLienDigestSettings = () => API.get(`/email-settings/lien-digest`);
export const updateLienDigestSettings = (data) => API.put(`/email-settings/lien-digest`, data);
export const sendLienDigestNow = () => API.post(`/email-settings/lien-digest/send-now`);
export const runLienDailyNow = () => API.post(`/email-settings/lien-daily/run-now`);

// ── Auth ──────────────────────────────────────────────────────────────
export const authLogin = (email, password) => API.post("/auth/login", { email, password });
export const authLogout = () => API.post("/auth/logout");
export const authMe = () => API.get("/auth/me");
export const authForgotPassword = (email) => API.post("/auth/forgot-password", { email });
export const authResetPassword = (token, new_password) => API.post("/auth/reset-password", { token, new_password });
// iter214 — preview-only bypass. Backend is the single source of truth
// for availability (env flag + hostname fence).
export const authBypassStatus = () => API.get("/auth/bypass-status");
export const authBypass = () => API.post("/auth/bypass");
// Magic-link (field staff) auth
export const magicStatus = () => API.get("/auth/magic/status");
export const magicRequest = (email) => API.post("/auth/magic/request", { email });
export const magicVerify = (token, device) => API.post("/auth/magic/verify", { token, device });
export const magicRenew = () => API.post("/auth/magic/renew");
export const magicLogout = () => API.post("/auth/magic/logout");
export const magicRequestAccess = (data) => API.post("/auth/magic/request-access", data);
export const listFieldUsers = () => API.get("/auth/magic/field-users");
export const inviteFieldUser = (data) => API.post("/auth/magic/invite", data);
export const resendFieldInvite = (id) => API.post(`/auth/magic/field-users/${id}/resend`);
export const revokeFieldUser = (id) => API.post(`/auth/magic/field-users/${id}/revoke`);
export const reinstateFieldUser = (id) => API.post(`/auth/magic/field-users/${id}/reinstate`);
export const changeFieldRole = (id, role) => API.patch(`/auth/magic/field-users/${id}/role`, { role });
export const removeFieldUser = (id) => API.delete(`/auth/magic/field-users/${id}`);
export const revokeFieldSession = (id, sid) => API.delete(`/auth/magic/field-users/${id}/sessions/${sid}`);
export const listAccessRequests = () => API.get("/auth/magic/access-requests");
export const decideAccessRequest = (id, decision) => API.post(`/auth/magic/access-requests/${id}/${decision}`);
export const getFieldAuthFlags = () => API.get("/auth/magic/flags");
export const putFieldAuthFlags = (data) => API.put("/auth/magic/flags", data);
export const getFieldAuthAudit = (limit = 100) => API.get("/auth/magic/audit", { params: { limit } });
export const listUsers = () => API.get("/auth/users");
// iter268 — access matrix (Settings → Users → Roles & Permissions)
export const getMyAccess = () => API.get("/access/me");
export const getAccessMatrix = () => API.get("/access/matrix");
export const setRoleAccess = (role, surfaces) => API.put(`/access/roles/${role}`, { surfaces });
export const resetRoleAccess = (role) => API.post(`/access/roles/${role}/reset`);
export const getUserAccess = (id) => API.get(`/access/users/${id}`);
export const setUserAccess = (id, surfaces) => API.put(`/access/users/${id}`, { surfaces });
export const diagnoseAccount = (email) => API.get(`/access/diagnose/${encodeURIComponent(email)}`);
// ── Global Invoicing repository (iter294) ──────────────────────────────
// Reads the same invoice records the WO section owns. Send/remind reuse
// the existing endpoints; nothing here edits an invoice.
const invoicingQuery = (params = {}) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) v.forEach(x => sp.append(k, x));
    else sp.append(k, v);
  });
  return sp.toString();
};
export const getInvoicingList = (params) => API.get(`/invoicing/list?${invoicingQuery(params)}`);
export const invoicingExportUrl = (params) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/invoicing/export?${invoicingQuery({ ...params, page: undefined, page_size: undefined })}`;
export const bulkInvoiceReminder = (data) => API.post("/invoicing/bulk-reminder", data);
export const getInvoicePayments = (id) => API.get(`/invoices/${id}/payments`);
export const recordInvoicePayment = (id, data) => API.post(`/invoices/${id}/payments`, data);
export const deleteInvoicePayment = (id, pid) => API.delete(`/invoices/${id}/payments/${pid}`);
export const getInvoicingTerms = () => API.get("/invoicing/terms");
export const setInvoicingTerms = (days) => API.put("/invoicing/terms", { payment_terms_days: days });

// Daily dispatch email (iter301) — techs get their day by email since
// push notifications aren't configured.
export const getDispatchEmailConfig = () => API.get("/dispatch-email/config");
export const setDispatchEmailConfig = (data) => API.put("/dispatch-email/config", data);
export const sendDispatchEmail = (data) => API.post("/dispatch-email/send", data);

// Field-test readiness (iter302) — shop geofence, tech home geofences,
// site coordinates, delivery switches.
export const getFieldReadiness = () => API.get("/dispatch/readiness");
export const geocodeTechHomes = () => API.post("/dispatch/readiness/geocode-homes");

export const createUser = (data) => API.post("/auth/users", data);
export const updateUser = (id, data) => API.patch(`/auth/users/${id}`, data);
export const deleteUser = (id, verifyToken) =>
  API.delete(`/auth/users/${id}`, verifyToken ? { params: { verify_token: verifyToken } } : undefined);

// Server-side passcode verification (C2). The frontend never knows the
// passcode value — it only collects the user's input and posts it here.
// On success the backend returns a one-shot `verify_token` that the
// next mutating call must include in its body.
export const verifyAdminPasscode = (passcode) => API.post("/admin/verify-passcode", { passcode });
export const setClientCustomerNumber = (clientId, customerNumber) =>
  API.patch(`/clients/${clientId}/customer-number`, { customer_number: customerNumber });
export const markCustomerNumberRequested = (clientId) =>
  API.patch(`/clients/${clientId}/customer-number-request`);

// ── MIRA by MARFI · AI feedback widget ──────────────────────────────
export const miraChat = (body) => API.post("/feedback/chat", body);
export const miraCaptureScreen = (body) => API.post("/feedback/capture-screen", body);
export const miraSubmit = (body) => API.post("/feedback/submit", body);
export const miraMyTickets = () => API.get("/feedback/my-tickets");
export const miraShipped = () => API.get("/feedback/shipped");

// iter231 — Foundation write-permission audit
export const getFoundationWritePermissionCheck = () =>
  API.get("/integrations/foundation/write-permission-check");

// Foundation job creation Phase 1 — sp_insert_update_jobs
export const getFoundationJobSpPreflight = () =>
  API.get("/integrations/foundation/job-sp/preflight");
export const pushFoundationJobSp = (job_no, description) =>
  API.post("/integrations/foundation/job-sp/push", { job_no, description });
export const getFoundationJobSpLog = (limit = 50) =>
  API.get("/integrations/foundation/job-sp/log", { params: { limit } });
// Foundation jobs field map — settings, reference sync, mappings
export const getFoundationSettings = () => API.get("/integrations/foundation/settings");
export const putFoundationSetting = (key, value, confirmText) =>
  API.put(`/integrations/foundation/settings/${key}`, { value, confirm_text: confirmText });
export const getFoundationSettingsAudit = (limit = 100) =>
  API.get("/integrations/foundation/settings/audit", { params: { limit } });
export const getFoundationRefStatus = () => API.get("/integrations/foundation/ref/status");
export const syncFoundationRef = (listId) =>
  API.post("/integrations/foundation/ref/sync", null, { params: listId ? { list_id: listId } : {} });
export const getFoundationRefRows = (listId, q, includeInactive = false) =>
  API.get(`/integrations/foundation/ref/${listId}`, { params: { q: q || undefined, include_inactive: includeInactive } });
export const getFoundationMappingOverview = () => API.get("/integrations/foundation/mapping/overview");
export const putFoundationMapping = (kind, key, code) =>
  API.put(`/integrations/foundation/mapping/${kind}`, { key, code });
export const previewWorkOrderFoundationSp = (woId, taxNo) =>
  API.get(`/work-orders/${woId}/push-to-foundation-sp/preview`, { params: taxNo ? { tax_no: taxNo } : {} });
export const pushWorkOrderToFoundationSp = (woId, source = "work_order", taxNo, phase = "full", completionDateMode = null) =>
  API.post(`/work-orders/${woId}/push-to-foundation-sp`, { source, tax_no: taxNo || null, phase, completion_date_mode: completionDateMode });
export const getFoundationBridgeUpdateStatus = () =>
  API.get("/integrations/foundation/bridge-update-status");
export const runFoundationBridgeSelfUpdate = () =>
  API.post("/integrations/foundation/bridge-self-update");
export const setFoundationCustomerNumber = (clientId, customerNumber) =>
  API.put("/integrations/foundation/customer-number", { client_id: clientId, customer_number: customerNumber });
export const getFoundationRefRaw = (listId) =>
  API.get(`/integrations/foundation/ref/${listId}/raw`);
export const getFoundationWoQueue = (params) =>
  API.get("/integrations/foundation/wo-queue", { params: params || {} });
export const dismissFoundationWoQueue = (woId, reason) =>
  API.post(`/integrations/foundation/wo-queue/${woId}/dismiss`, { reason: reason || "" });
export const restoreFoundationWoQueue = (woId) =>
  API.post(`/integrations/foundation/wo-queue/${woId}/restore`);


// iter231 — Procurement receiving / check-in
export const getProcurementPOs = (status) =>
  API.get("/procurement/pos", { params: status ? { status } : {} });
export const checkInProcurementPO = (poId, items, notes, reasons = []) =>
  API.post(`/procurement/pos/${poId}/check-in`, { items, notes: notes || "", reasons });
// Field completion → PM review (Ops dashboard)
export const getFieldCompletions = (includeAcknowledged = false) =>
  API.get("/dashboard/field-completions", { params: { include_acknowledged: includeAcknowledged } });
export const acknowledgeFieldCompletion = (woId) => API.post(`/work-orders/${woId}/field-complete/acknowledge`);
export const unacknowledgeFieldCompletion = (woId) => API.delete(`/work-orders/${woId}/field-complete/acknowledge`);
export const createStockPO = (data) => API.post("/procurement/stock-pos", data);

// iter231 — Miter nightly auto-pull
export const triggerMiterNightlySync = () => API.post("/miter/nightly-sync");
export const getMiterNightlySyncStatus = () => API.get("/miter/nightly-sync-status");

// iter233 — DOT inspections
export const getDotRequired = (params) => API.get("/dashboard/dot-required", { params });
export const setWODotRequired = (woId, required) =>
  API.patch(`/work-orders/${woId}/dot-required`, { required });

export default API;


export const sendDotDigest = (body) => API.post("/dot/compliance-digest", body || {});

export const getCleanupCandidates = () => API.get("/admin/cleanup/candidates");
export const previewCleanup = (body) => API.post("/admin/cleanup/preview", body);
export const executeCleanup = (body) => API.post("/admin/cleanup/execute", body);
export const previewFullWipe = () => API.get("/admin/wipe-transactional/preview");
export const executeFullWipe = (confirm) => API.post("/admin/wipe-transactional/execute", { confirm });

export const getSalesAnalytics = (am) => API.get("/sales/analytics", { params: am ? { am } : {} });
export const updateSalesGoals = (body) => API.put("/sales/goals", body);

// iter247 — Commissions Dashboard (locked to Ryan / Nathan / Mary)
export const getCommissionsAccess = () => API.get("/commissions/access");
export const getCommissionsDashboard = (fy) => API.get("/commissions/dashboard", { params: fy != null ? { fy } : {} });
export const getCommissionsProjects = (fy, person) =>
  API.get("/commissions/projects", { params: { ...(fy != null ? { fy } : {}), ...(person ? { person } : {}) } });
export const setCommissionOverride = (proposalId, body) => API.patch(`/commissions/projects/${proposalId}`, body);
export const markCommissionPaid = (month, person, paid) =>
  API.post(`/commissions/months/${month}/${person}/mark-paid`, { paid });
export const commissionExportUrl = (fy, person, level) =>
  `${API.defaults.baseURL}/commissions/export?fy=${fy}&person=${person}&level=${level}`;
// iter251 — One-click PDF commission statement per (person, month).
// `download=1` → browser saves as attachment; `download=0` → inline preview.
export const commissionStatementUrl = (person, month, download = 1) =>
  `${API.defaults.baseURL}/commissions/statement.pdf?person=${person}&month=${month}&download=${download}`;

// iter327 — Foundation actuals read-back (READ ONLY)
export const getFoundationActuals = (woId) => API.get(`/work-orders/${woId}/foundation-jc-actuals`);
export const getFoundationActualsDetail = (woId) => API.get(`/work-orders/${woId}/foundation-jc-actuals/detail`);
export const refreshFoundationActuals = (woId, asOf) =>
  API.post(`/work-orders/${woId}/foundation-jc-actuals/refresh`, null, { params: asOf ? { as_of: asOf } : {} });
export const getFoundationPaid = (invoiceNumbers) =>
  API.get("/invoices/foundation-paid", { params: { invoice_numbers: (invoiceNumbers || []).join(",") } });
